const CONFIRMATION_SOUND_KEY = "banculem-confirmation-sound";

interface SoundStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ConfirmationAudioContext {
  readonly currentTime: number;
  readonly state: AudioContextState;
  readonly destination: AudioNode;
  createOscillator(): OscillatorNode;
  createGain(): GainNode;
  resume(): Promise<void>;
}

export interface ConfirmationSoundOptions {
  force?: boolean;
  storage?: SoundStorage | null;
  contextFactory?: () => ConfirmationAudioContext | null;
}

let sharedContext: ConfirmationAudioContext | null = null;

function browserStorage(): SoundStorage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function browserAudioContext(): ConfirmationAudioContext | null {
  if (typeof window === "undefined") return null;
  const audioWindow = window as Window & { webkitAudioContext?: typeof AudioContext };
  const AudioContextConstructor = window.AudioContext ?? audioWindow.webkitAudioContext;
  if (!AudioContextConstructor) return null;
  return new AudioContextConstructor();
}

export function confirmationSoundEnabled(storage: SoundStorage | null = browserStorage()): boolean {
  try {
    return storage?.getItem(CONFIRMATION_SOUND_KEY) === "true";
  } catch {
    return false;
  }
}

export function setConfirmationSoundEnabled(enabled: boolean, storage: SoundStorage | null = browserStorage()): void {
  try {
    storage?.setItem(CONFIRMATION_SOUND_KEY, String(enabled));
  } catch {
    // A blocked local preference must never interfere with a financial action.
  }
}

export function confirmationSoundSupported(): boolean {
  if (typeof window === "undefined") return false;
  const audioWindow = window as Window & { webkitAudioContext?: typeof AudioContext };
  return Boolean(window.AudioContext ?? audioWindow.webkitAudioContext);
}

function scheduleNote(context: ConfirmationAudioContext, frequency: number, startsAt: number, duration: number, volume: number): void {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(frequency, startsAt);
  gain.gain.setValueAtTime(0.0001, startsAt);
  gain.gain.exponentialRampToValueAtTime(volume, startsAt + 0.018);
  gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + duration);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(startsAt);
  oscillator.stop(startsAt + duration + 0.015);
}

export async function playConfirmationSound(options: ConfirmationSoundOptions = {}): Promise<boolean> {
  const storage = options.storage === undefined ? browserStorage() : options.storage;
  if (!options.force && !confirmationSoundEnabled(storage)) return false;

  try {
    const context = options.contextFactory
      ? options.contextFactory()
      : (sharedContext ??= browserAudioContext());
    if (!context || context.state === "closed") return false;
    if (context.state !== "running") await context.resume();

    const startsAt = context.currentTime + 0.012;
    scheduleNote(context, 523.25, startsAt, 0.13, 0.032);
    scheduleNote(context, 659.25, startsAt + 0.085, 0.17, 0.04);
    return true;
  } catch {
    // Audio feedback is optional and must never fail or roll back an operation.
    return false;
  }
}
